/*
 * Copyright (c) 2026 Gabriele Bonora. Tutti i diritti riservati.
 *
 * Software proprietario. Nessuna licenza d'uso, copia, modifica o distribuzione
 * e' concessa in assenza di autorizzazione scritta del titolare dei diritti.
 * Vedere il file LICENSE nella radice del progetto.
 */
import { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { Plus, Search, X, MapPin, Clock, User, AlertTriangle, ChevronRight, Calendar, Printer } from 'lucide-react';
import AdminLayout from '../components/AdminLayout';
import RegistroPdfModal from '../components/RegistroPdfModal';
import { interventiAPI } from '../api/interventi';
import { utentiAPI } from '../api/utenti';
import { fmtDataOra } from '../utils/date';
import { isoLocale } from '../utils/periodo';

const ACCENT = '#8B5CF6';

// I filtri restano finche' la scheda e' aperta: aprire un intervento smonta la
// pagina, e al ritorno bisognerebbe riscegliere il cliente ogni volta.
const CHIAVE_FILTRI = 'tce_interventi_filtri';
const FILTRI_VUOTI = { stato: '', priorita: '', cliente_id: '', tecnico_id: '', q: '', interno: false, page: 1 };

function filtriSalvati() {
  try {
    return { ...FILTRI_VUOTI, ...JSON.parse(sessionStorage.getItem(CHIAVE_FILTRI) || '{}') };
  } catch {
    return FILTRI_VUOTI;
  }
}
const CATEGORIE_INTERNE = { magazzino: 'Magazzino', manutenzione_automezzi: 'Manutenzione automezzi', formazione: 'Formazione', riunione: 'Riunione', altro: 'Altro' };

const PRIORITA_STYLE = {
  urgente: { bg: '#FEE2E2', color: '#991B1B', label: 'Urgente', icon: true },
  alta:    { bg: '#FED7AA', color: '#9A3412', label: 'Alta' },
  normale: { bg: '#E0E7FF', color: '#3730A3', label: 'Normale' },
  bassa:   { bg: '#F3F4F6', color: '#4B5563', label: 'Bassa' },
};

export default function Interventi() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const [filters, setFilters] = useState(filtriSalvati);
  const [creating, setCreating] = useState(false);
  const [stampa, setStampa] = useState(false);

  useEffect(() => {
    try {
      sessionStorage.setItem(CHIAVE_FILTRI, JSON.stringify(filters));
    } catch { /* senza storage i filtri valgono solo finche' si resta sulla pagina */ }
  }, [filters]);

  const { data: result = { data: [], total: 0 }, isLoading } = useQuery({
    queryKey: ['interventi', filters],
    queryFn: () => interventiAPI.list({
      ...filters,
      stato: filters.stato || undefined,
      priorita: filters.priorita || undefined,
      cliente_id: filters.cliente_id || undefined,
      tecnico_id: filters.tecnico_id || undefined,
      q: filters.q || undefined,
      interno: filters.interno || undefined,
    }),
  });

  const { data: stats } = useQuery({
    queryKey: ['interventi-stats'],
    queryFn: () => interventiAPI.stats(),
  });

  const { data: clienti = [] } = useQuery({
    queryKey: ['utenti', 'cliente'],
    queryFn: () => utentiAPI.list('cliente'),
  });

  const { data: tecnici = [] } = useQuery({
    queryKey: ['utenti', 'tecnico'],
    queryFn: () => utentiAPI.list('tecnico'),
  });

  const totalPages = Math.ceil(result.total / 20);

  return (
    <AdminLayout title="Interventi">
      {/* Statistiche per stato */}
      {stats && (
        <div style={styles.statsRow}>
          {stats.by_stato.map((s) => (
            <button
              key={s.nome}
              onClick={() => setFilters({ ...filters, stato: filters.stato === s.nome ? '' : s.nome, page: 1 })}
              style={{
                ...styles.statCard,
                ...(filters.stato === s.nome ? { ...styles.statCardActive, borderColor: s.colore } : {}),
                borderLeft: `4px solid ${s.colore}`,
              }}
            >
              <div style={styles.statLabel}>{statoLabel(s.nome)}</div>
              <div style={styles.statValue}>{s.totale}</div>
            </button>
          ))}
        </div>
      )}

      {/* Toolbar superiore */}
      <div style={styles.toolbar}>
        <div style={styles.searchWrapper}>
          <Search size={16} style={styles.searchIcon} />
          <input
            type="text"
            placeholder="Cerca per codice, titolo o descrizione..."
            value={filters.q}
            onChange={(e) => setFilters({ ...filters, q: e.target.value, page: 1 })}
            style={styles.searchInput}
          />
        </div>
        <button onClick={() => setStampa(true)} style={styles.registroBtn}>
          <Printer size={16} /> Registro lavori (PDF)
        </button>
        <button onClick={() => setCreating(true)} style={styles.addBtn}>
          <Plus size={16} /> Nuovo intervento
        </button>
      </div>

      {stampa && (
        <RegistroPdfModal
          dal={isoLocale(new Date(new Date().getFullYear(), new Date().getMonth(), 1))}
          al={isoLocale(new Date())}
          tecnico={filters.tecnico_id}
          nomeTecnico={(() => {
            const t = tecnici.find((x) => x.id === filters.tecnico_id);
            return t ? `${t.nome} ${t.cognome}` : '';
          })()}
          nascosti={[]}
          clienteIniziale={filters.cliente_id}
          fonteFiltri="della lista"
          onChiudi={() => setStampa(false)}
        />
      )}

      {/* Filtri secondari */}
      <div style={styles.filtersBar}>
        <select
          value={filters.priorita}
          onChange={(e) => setFilters({ ...filters, priorita: e.target.value, page: 1 })}
          style={styles.select}
        >
          <option value="">Tutte le priorità</option>
          <option value="urgente">Urgente</option>
          <option value="alta">Alta</option>
          <option value="normale">Normale</option>
          <option value="bassa">Bassa</option>
        </select>

        <select
          value={filters.cliente_id}
          onChange={(e) => setFilters({ ...filters, cliente_id: e.target.value, page: 1 })}
          style={styles.select}
        >
          <option value="">Tutti i clienti</option>
          {clienti.map((c) => (
            <option key={c.id} value={c.id}>{c.ragione_sociale || `${c.nome || ''} ${c.cognome || ''}`.trim() || '(cliente senza nome)'}</option>
          ))}
        </select>

        <select
          value={filters.tecnico_id}
          onChange={(e) => setFilters({ ...filters, tecnico_id: e.target.value, page: 1 })}
          style={styles.select}
        >
          <option value="">Tutti i tecnici</option>
          {tecnici.map((t) => (
            <option key={t.id} value={t.id}>{t.nome} {t.cognome}</option>
          ))}
        </select>

        <button
          onClick={() => setFilters({ ...filters, interno: !filters.interno, page: 1 })}
          style={{ ...styles.internoFilterBtn, ...(filters.interno ? styles.internoFilterBtnActive : {}) }}
        >
          Solo lavoro interno
        </button>

        {(filters.stato || filters.priorita || filters.cliente_id || filters.tecnico_id || filters.q || filters.interno) && (
          <button
            onClick={() => setFilters(FILTRI_VUOTI)}
            style={styles.clearFiltersBtn}
          >
            <X size={14} /> Pulisci filtri
          </button>
        )}
      </div>

      {/* Lista interventi */}
      <div style={styles.list}>
        {isLoading && <div style={styles.empty}>Caricamento...</div>}
        {!isLoading && result.data.length === 0 && (
          <div style={styles.empty}>
            <Clock size={40} style={{ marginBottom: '0.5rem', color: '#9CA3AF' }} />
            <p>Nessun intervento trovato.</p>
            <button onClick={() => setCreating(true)} style={styles.addBtnEmpty}>
              <Plus size={16} /> Crea il primo intervento
            </button>
          </div>
        )}

        {result.data.map((i) => {
          const prio = PRIORITA_STYLE[i.priorita] || PRIORITA_STYLE.normale;
          return (
            <div
              key={i.id}
              onClick={() => navigate(`/interventi/${i.id}`)}
              style={styles.interventoCard}
            >
              <div style={styles.cardLeft}>
                <div style={styles.cardHeader}>
                  <div style={{ ...styles.statoBadge, backgroundColor: i.stato_colore + '22', color: i.stato_colore }}>
                    {statoLabel(i.stato)}
                  </div>
                  <div style={{ ...styles.prioBadge, backgroundColor: prio.bg, color: prio.color }}>
                    {prio.icon && <AlertTriangle size={12} />}
                    {prio.label}
                  </div>
                  <code style={styles.codice}>{i.codice}</code>
                </div>
                <h3 style={styles.cardTitle}>{i.titolo}</h3>
                {i.descrizione && <p style={styles.cardDesc}>{i.descrizione.substring(0, 140)}{i.descrizione.length > 140 ? '...' : ''}</p>}
                <div style={styles.cardMeta}>
                  {i.cliente_id ? (
                    <span><User size={13} /> {i.cliente_nome} {i.cliente_cognome}</span>
                  ) : (
                    <span style={styles.internoBadge}>Lavoro interno{i.categoria_interna ? ` · ${CATEGORIE_INTERNE[i.categoria_interna] || i.categoria_interna}` : ''}</span>
                  )}
                  {i.citta && <span><MapPin size={13} /> {i.citta}</span>}
                  {i.data_appuntamento && <span><Calendar size={13} /> {fmtDataOra(i.data_appuntamento)}</span>}
                  {!i.data_appuntamento && <span style={{ color: '#9CA3AF' }}><Calendar size={13} /> Non programmato</span>}
                </div>
                {i.tecnici && i.tecnici.length > 0 && (
                  <div style={styles.tecniciRow}>
                    Tecnici: {i.tecnici.map((t, idx) => (
                      <span key={t.id} style={styles.tecnicoChip}>
                        {t.nome} {t.cognome}
                        {t.ruolo_intervento !== 'principale' && ` (${t.ruolo_intervento})`}
                      </span>
                    ))}
                  </div>
                )}
                {(!i.tecnici || i.tecnici.length === 0) && (
                  <div style={styles.noTecnico}>⚠ Nessun tecnico assegnato</div>
                )}
              </div>
              <div style={styles.cardRight}>
                <ChevronRight size={20} color="#9CA3AF" />
              </div>
            </div>
          );
        })}
      </div>

      {/* Paginazione */}
      {totalPages > 1 && (
        <div style={styles.pagination}>
          <button
            onClick={() => setFilters({ ...filters, page: Math.max(1, filters.page - 1) })}
            disabled={filters.page === 1}
            style={styles.pageBtn}
          >
            ← Precedente
          </button>
          <span style={styles.pageInfo}>
            Pagina {filters.page} di {totalPages} · {result.total} totali
          </span>
          <button
            onClick={() => setFilters({ ...filters, page: Math.min(totalPages, filters.page + 1) })}
            disabled={filters.page >= totalPages}
            style={styles.pageBtn}
          >
            Successiva →
          </button>
        </div>
      )}

      {creating && (
        <NuovoInterventoModal
          clienti={clienti}
          onClose={() => setCreating(false)}
          onCreated={(nuovo) => {
            setCreating(false);
            queryClient.invalidateQueries({ queryKey: ['interventi'] });
            queryClient.invalidateQueries({ queryKey: ['interventi-stats'] });
            navigate(`/interventi/${nuovo.id}`);
          }}
        />
      )}
    </AdminLayout>
  );
}

function statoLabel(nome) {
  const labels = {
    in_attesa: 'In attesa',
    confermato: 'Confermato',
    in_corso: 'In corso',
    completato: 'Completato',
    annullato: 'Annullato',
  };
  return labels[nome] || nome;
}

function NuovoInterventoModal({ clienti, onClose, onCreated }) {
  const [form, setForm] = useState({
    cliente_id: '',
    titolo: '',
    descrizione: '',
    tipo_problema: '',
    priorita: 'normale',
    indirizzo: '',
    citta: '',
    cap: '',
    data_appuntamento: '',
    note_cliente: '',
  });
  const [error, setError] = useState('');

  const createMutation = useMutation({
    mutationFn: interventiAPI.create,
    onSuccess: onCreated,
    onError: (err) => setError(err.response?.data?.error || 'Errore nella creazione'),
  });

  const handle = (field) => (e) => {
    setForm({ ...form, [field]: e.target.value });
    setError('');
  };

  // Pre-compila indirizzo quando scegli cliente
  const onClienteChange = (e) => {
    const id = e.target.value;
    const cliente = clienti.find(c => c.id === id);
    setForm({
      ...form,
      cliente_id: id,
      indirizzo: cliente?.indirizzo || form.indirizzo,
      citta: cliente?.citta || form.citta,
    });
  };

  const submit = (e) => {
    e.preventDefault();
    setError('');
    if (!form.cliente_id) {
      setError('Seleziona un cliente');
      return;
    }
    createMutation.mutate({
      ...form,
      data_appuntamento: form.data_appuntamento || null,
    });
  };

  return (
    <div style={styles.modalOverlay} onClick={onClose}>
      <div style={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div style={styles.modalHeader}>
          <h2 style={{ margin: 0 }}>Nuovo intervento</h2>
          <button onClick={onClose} style={styles.closeBtn}><X size={20} /></button>
        </div>

        <form onSubmit={submit} style={styles.form}>
          <div style={styles.field}>
            <label style={styles.label}>Cliente *</label>
            <select value={form.cliente_id} onChange={onClienteChange} style={styles.input} required>
              <option value="">— Seleziona cliente —</option>
              {clienti.map((c) => (
                <option key={c.id} value={c.id}>{c.ragione_sociale || `${c.nome || ''} ${c.cognome || ''}`.trim() || '(cliente senza nome)'} {c.telefono ? `· ${c.telefono}` : ''}</option>
              ))}
            </select>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Titolo *</label>
            <input value={form.titolo} onChange={handle('titolo')} style={styles.input} required placeholder="es. Perdita sotto al lavandino" />
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Descrizione</label>
            <textarea value={form.descrizione} onChange={handle('descrizione')} style={{ ...styles.input, minHeight: '70px', fontFamily: 'inherit' }} placeholder="Dettagli del problema..." />
          </div>

          <div style={styles.row}>
            <div style={styles.field}>
              <label style={styles.label}>Tipo problema</label>
              <input value={form.tipo_problema} onChange={handle('tipo_problema')} style={styles.input} placeholder="es. Perdita, Caldaia, Sturatura..." />
            </div>
            <div style={styles.field}>
              <label style={styles.label}>Priorità</label>
              <select value={form.priorita} onChange={handle('priorita')} style={styles.input}>
                <option value="bassa">Bassa</option>
                <option value="normale">Normale</option>
                <option value="alta">Alta</option>
                <option value="urgente">Urgente</option>
              </select>
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Indirizzo intervento</label>
            <input value={form.indirizzo} onChange={handle('indirizzo')} style={styles.input} placeholder="Via, numero civico" />
          </div>

          <div style={styles.row}>
            <div style={{ ...styles.field, flex: 2 }}>
              <label style={styles.label}>Città</label>
              <input value={form.citta} onChange={handle('citta')} style={styles.input} />
            </div>
            <div style={{ ...styles.field, flex: 1 }}>
              <label style={styles.label}>CAP</label>
              <input value={form.cap} onChange={handle('cap')} style={styles.input} />
            </div>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Data e ora appuntamento</label>
            <input type="datetime-local" value={form.data_appuntamento} onChange={handle('data_appuntamento')} style={styles.input} />
            <small style={styles.hint}>Lascia vuoto se urgenza / da concordare</small>
          </div>

          <div style={styles.field}>
            <label style={styles.label}>Note per il cliente</label>
            <textarea value={form.note_cliente} onChange={handle('note_cliente')} style={{ ...styles.input, minHeight: '50px', fontFamily: 'inherit' }} />
          </div>

          {error && <div style={styles.errorBox}>{error}</div>}

          <div style={styles.modalActions}>
            <button type="button" onClick={onClose} style={styles.cancelBtn}>Annulla</button>
            <button type="submit" disabled={createMutation.isPending} style={styles.submitBtn}>
              {createMutation.isPending ? 'Creazione...' : 'Crea intervento'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

const styles = {
  statsRow: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: '0.75rem', marginBottom: '1.5rem' },
  statCard: { backgroundColor: 'white', padding: '1rem 1.25rem', borderRadius: '10px', border: '2px solid transparent', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', cursor: 'pointer', textAlign: 'left', fontFamily: 'inherit' },
  statCardActive: { backgroundColor: '#F9FAFB' },
  statLabel: { fontSize: '0.75rem', color: '#6B7280', textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600 },
  statValue: { fontSize: '1.6rem', fontWeight: 700, color: '#111827', marginTop: '0.3rem' },

  toolbar: { display: 'flex', gap: '0.75rem', marginBottom: '0.75rem', flexWrap: 'wrap', alignItems: 'center' },
  searchWrapper: { position: 'relative', flex: 1, minWidth: '240px' },
  searchIcon: { position: 'absolute', left: '0.85rem', top: '50%', transform: 'translateY(-50%)', color: '#9CA3AF' },
  searchInput: { width: '100%', padding: '0.6rem 0.85rem 0.6rem 2.3rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.92rem', outline: 'none' },
  addBtn: { display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1.1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
  registroBtn: { display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.6rem 1.1rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontWeight: 600, fontFamily: 'inherit' },

  filtersBar: { display: 'flex', gap: '0.5rem', marginBottom: '1.25rem', flexWrap: 'wrap', alignItems: 'center' },
  select: { padding: '0.5rem 0.7rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.88rem', outline: 'none', backgroundColor: 'white', cursor: 'pointer', fontFamily: 'inherit' },
  clearFiltersBtn: { display: 'flex', alignItems: 'center', gap: '0.3rem', padding: '0.5rem 0.8rem', backgroundColor: 'transparent', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', color: '#6B7280' },
  internoFilterBtn: { padding: '0.5rem 0.8rem', backgroundColor: 'white', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontSize: '0.85rem', fontWeight: 500, color: '#4B5563', fontFamily: 'inherit' },
  internoFilterBtnActive: { backgroundColor: '#EDE9FE', color: '#5B21B6', borderColor: '#5B21B6' },
  internoBadge: { backgroundColor: '#EDE9FE', color: '#5B21B6', padding: '0.15rem 0.55rem', borderRadius: '6px', fontWeight: 600, fontSize: '0.8rem', display: 'inline-flex', alignItems: 'center' },

  list: { display: 'flex', flexDirection: 'column', gap: '0.65rem' },
  empty: { padding: '4rem 2rem', textAlign: 'center', color: '#6B7280', display: 'flex', flexDirection: 'column', alignItems: 'center', backgroundColor: 'white', borderRadius: '12px' },
  addBtnEmpty: { marginTop: '1rem', display: 'flex', alignItems: 'center', gap: '0.4rem', padding: '0.55rem 1.1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },

  interventoCard: { display: 'flex', backgroundColor: 'white', padding: '1.1rem 1.25rem', borderRadius: '12px', cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.04)', gap: '1rem', alignItems: 'center' },
  cardLeft: { flex: 1, minWidth: 0 },
  cardRight: { flexShrink: 0 },
  cardHeader: { display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.55rem', flexWrap: 'wrap' },
  statoBadge: { padding: '0.22rem 0.7rem', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 700 },
  prioBadge: { display: 'inline-flex', alignItems: 'center', gap: '0.25rem', padding: '0.18rem 0.55rem', borderRadius: '6px', fontSize: '0.74rem', fontWeight: 600 },
  codice: { backgroundColor: '#F3F4F6', padding: '0.18rem 0.5rem', borderRadius: '4px', fontSize: '0.8rem', fontFamily: 'ui-monospace, monospace', color: '#374151' },
  cardTitle: { margin: '0 0 0.3rem', fontSize: '1.02rem', fontWeight: 600, color: '#111827' },
  cardDesc: { margin: '0 0 0.55rem', color: '#6B7280', fontSize: '0.9rem' },
  cardMeta: { display: 'flex', flexWrap: 'wrap', gap: '1rem', fontSize: '0.85rem', color: '#4B5563' },
  tecniciRow: { marginTop: '0.55rem', fontSize: '0.85rem', color: '#4B5563', display: 'flex', flexWrap: 'wrap', gap: '0.4rem', alignItems: 'center' },
  tecnicoChip: { backgroundColor: '#EDE9FE', color: '#5B21B6', padding: '0.15rem 0.5rem', borderRadius: '6px', fontSize: '0.78rem', fontWeight: 500 },
  noTecnico: { marginTop: '0.55rem', fontSize: '0.83rem', color: '#92400E' },

  pagination: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '1rem', marginTop: '1.5rem', flexWrap: 'wrap' },
  pageBtn: { padding: '0.5rem 1rem', backgroundColor: 'white', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontSize: '0.88rem' },
  pageInfo: { fontSize: '0.88rem', color: '#6B7280' },

  modalOverlay: { position: 'fixed', top: 0, bottom: 0, left: 0, right: 0, backgroundColor: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 50, padding: '1rem' },
  modal: { backgroundColor: 'white', borderRadius: '12px', width: '100%', maxWidth: '600px', maxHeight: '90vh', overflow: 'auto' },
  modalHeader: { padding: '1.25rem 1.5rem', borderBottom: '1px solid #E5E7EB', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
  closeBtn: { background: 'none', border: 'none', cursor: 'pointer', color: '#6B7280' },
  form: { padding: '1.25rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' },
  field: { display: 'flex', flexDirection: 'column', gap: '0.3rem', flex: 1, minWidth: '150px' },
  row: { display: 'flex', gap: '0.75rem', flexWrap: 'wrap' },
  label: { fontSize: '0.82rem', fontWeight: 600, color: '#374151' },
  input: { padding: '0.55rem 0.75rem', border: '1px solid #D1D5DB', borderRadius: '8px', fontSize: '0.95rem', outline: 'none', width: '100%' },
  hint: { fontSize: '0.75rem', color: '#6B7280' },
  errorBox: { backgroundColor: '#FEE2E2', color: '#991B1B', padding: '0.65rem', borderRadius: '8px', fontSize: '0.85rem' },
  modalActions: { display: 'flex', gap: '0.75rem', justifyContent: 'flex-end', marginTop: '0.5rem' },
  cancelBtn: { padding: '0.55rem 1.1rem', backgroundColor: 'white', color: '#374151', border: '1px solid #D1D5DB', borderRadius: '8px', cursor: 'pointer', fontWeight: 500 },
  submitBtn: { padding: '0.55rem 1.1rem', backgroundColor: ACCENT, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 600 },
};
